import { APP_ROUTE_NAMES } from '@/configs/routes';

export type LandingFeatureIconKey =
  | 'search'
  | 'people'
  | 'inventory'
  | 'storefront'
  | 'assignment'
  | 'biotech'
  | 'science'
  | 'verified';

export type LandingFeatureItem = {
  icon: LandingFeatureIconKey;
  title: string;
  description: string;
};

export type LandingGetStartedCard = {
  id: 'search' | 'registry';
  headline: string;
  tagline: string;
};

export type LandingDeepRow = {
  id: 'search' | 'registry';
  eyebrow: string;
  title: string;
  description: string;
  bullets: string[];
};

export const landingHero = {
  title: 'Search. Discover. Decide.',
  subtitle:
    'BreedLOGIC is a registry-first platform for breed societies—animal records, members, and registrations at the centre—with public discovery when your communities need it.',
  primaryCtaSearch: APP_ROUTE_NAMES.SEARCH,
  primaryCtaRegistry: APP_ROUTE_NAMES.REGISTRY,
};

export const landingTrustChips = [
  'Breed societies',
  'Registry & records',
  'Public discovery',
];

export const landingFeatureSection = {
  eyebrow: 'Platform',
  title: 'Everything you need to run the registry—and reach your community',
  subtitle:
    'BreedLOGIC is built registry-first: authoritative animal and member data, registrar workflows, then public search and catalogues when you want the wider industry to discover you.',
};

export const landingFeatures: LandingFeatureItem[] = [
  {
    icon: 'inventory',
    title: 'Animal records',
    description:
      'Structured pedigrees and identifiers in one place—easy to read, share, and govern as the registry of record.',
  },
  {
    icon: 'assignment',
    title: 'Registrations & admin',
    description:
      'Registry teams manage registrations and society data with consistent tools.',
  },
  {
    icon: 'people',
    title: 'Member profiles',
    description:
      'Member context alongside animals so registrars and members work from the same trusted picture.',
  },
  {
    icon: 'verified',
    title: 'Built for trust',
    description:
      'Designed for societies and industry partners who rely on accurate, authoritative data.',
  },
  {
    icon: 'science',
    title: 'Breeding insight',
    description:
      'Access the breeding context you need to compare animals and plan with confidence, with powerful tools like our Mating Predictor.',
  },
  {
    icon: 'biotech',
    title: 'EBVs & genetic merit',
    description:
      'Estimated breeding values sit alongside authoritative pedigrees and registry data, so comparison and selection stay grounded in trusted records.',
  },
  {
    icon: 'search',
    title: 'Powerful database search',
    description:
      'Find animals and members quickly with filters built for breeders, buyers, and agents.',
  },
  {
    icon: 'storefront',
    title: 'Sale catalogues',
    description:
      'Browse listings and connect discovery to real-world buying and selling.',
  },
];

export const landingGetStartedSection = {
  eyebrow: 'Get started',
  title: 'Search the database or manage the registry',
  subtitle:
    'Database Search opens discovery to breeders, buyers, and agents; Registry is the core of BreedLOGIC.',
};

export const landingGetStartedCards: LandingGetStartedCard[] = [
  {
    id: 'search',
    headline: APP_ROUTE_NAMES.SEARCH,
    tagline:
      'The search hub for breeders, buyers, and agents to find animals, members, and more.',
  },
  {
    id: 'registry',
    headline: APP_ROUTE_NAMES.REGISTRY,
    tagline:
      'The management portal for breed registrars and staff to manage animals, members, and more.',
  },
];

export const landingDeepSection = {
  eyebrow: 'Details',
  title: 'Go deeper on Search and Registry',
  subtitle:
    'Public search extends your data to the wider industry; registry workflows anchor the platform.',
};

export const landingDeepRows: LandingDeepRow[] = [
  {
    id: 'search',
    eyebrow: APP_ROUTE_NAMES.SEARCH,
    title: 'Discovery without friction',
    description:
      'Surface the right animals and members for your scenario—whether you are buying, selling, or researching pedigrees.',
    bullets: [
      'Purpose-built search for livestock records',
      'Context for members alongside animals',
      'Catalogues and listings when societies publish them',
      'Fast paths to the detail you care about',
    ],
  },
  {
    id: 'registry',
    eyebrow: APP_ROUTE_NAMES.REGISTRY,
    title: 'Operations that stay organised',
    description:
      'Give registrars and administrators a focused workspace for society data, day to day.',
    bullets: [
      'Animals, members, and registrations in one portal',
      'Workflows aligned to breed society operations',
      'Secure access through your organisation',
      'Less context switching, more time for members',
    ],
  },
];

export type LandingComingSoonItem = {
  title: string;
  /** Shown as a chip; tentative / indicative only */
  timingLabel: string;
  description: string;
};

export const landingComingSoonSection = {
  title: 'Coming later this year!',
  subtitle:
    'A preview of what we are building next. Timelines are tentative and may change as we work with breed societies.',
};

export const landingComingSoonItems: LandingComingSoonItem[] = [
  {
    title: 'Member workflows for seedstock',
    timingLabel: 'Q3 2026 (tentative)',
    description:
      'The new BreedLOGIC member workflow introduces a modern, streamlined experience for how members interact with the platform. It delivers clearer role-based access, structured approvals, and intuitive user management, making it easier for members to manage animal records, registrations, transfers, and enterprise relationships with confidence. Built to support everyday tasks and governance at scale, this workflow modernises member interactions while laying the foundation for future enhancements.',
  },
  {
    title: 'Invoicing',
    timingLabel: 'Q4 2026 (tentative)',
    description:
      'Generate invoices and credit notes for registrations, transfers, and disposals. Integrate with Xero API. Allow export for societies not using Xero. Generate annual membership and inventory invoices, communicate with breeders on current inventory, receive feedback on disposals, transfers, registrations, and support batch invoicing.',
  },
];

export const landingClosingCta = {
  title: 'Ready to explore BreedLOGIC?',
  subtitle:
    'Jump into public Database Search anytime, or open Registry when your society has granted you access.',
  searchLabel: `Open ${APP_ROUTE_NAMES.SEARCH}`,
  registryLabel: `Go to ${APP_ROUTE_NAMES.REGISTRY}`,
};
